import React, { useState, useEffect, useRef } from "react";

type DatePickerProps = {
  selectedDate: Date | null;
  onSelect: (date: Date) => void;
  onClose: () => void;
  minDate?: Date;
  maxDate?: Date;
};

export default function DatePicker({ selectedDate, onSelect, onClose, minDate, maxDate }: DatePickerProps) {
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1) : new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day: number) => {
    const selected = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    selected.setHours(0, 0, 0, 0);
    
    if (minDate) {
      const minDateNormalized = new Date(minDate);
      minDateNormalized.setHours(0, 0, 0, 0);
      if (selected < minDateNormalized) {
        return;
      }
    }
    if (maxDate) {
      const maxDateNormalized = new Date(maxDate);
      maxDateNormalized.setHours(0, 0, 0, 0);
      if (selected > maxDateNormalized) {
        return;
      }
    }
    
    onSelect(selected);
  };

  const isDateDisabled = (day: number): boolean => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    date.setHours(0, 0, 0, 0);
    
    if (minDate) {
      const minDateNormalized = new Date(minDate);
      minDateNormalized.setHours(0, 0, 0, 0);
      if (date < minDateNormalized) {
        return true;
      }
    }
    if (maxDate) {
      const maxDateNormalized = new Date(maxDate);
      maxDateNormalized.setHours(0, 0, 0, 0);
      if (date > maxDateNormalized) {
        return true;
      }
    }
    return false;
  };

  const isDateSelected = (day: number): boolean => {
    if (!selectedDate) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDayOfMonth }, (_, i) => i);

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  return (
    <div className="date-picker" ref={pickerRef}>
      <div className="date-picker-header">
        <button className="date-picker-nav" onClick={handlePrevMonth} type="button">
          ‹
        </button>
        <span className="date-picker-month">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button className="date-picker-nav" onClick={handleNextMonth} type="button">
          ›
        </button>
      </div>
      
      <div className="date-picker-weekdays">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <div key={day} className="date-picker-weekday">
            {day}
          </div>
        ))}
      </div>

      <div className="date-picker-days">
        {emptyDays.map((i) => (
          <div key={`empty-${i}`} className="date-picker-day empty" />
        ))}
        {days.map((day) => (
          <button
            key={day}
            type="button"
            className={`date-picker-day ${isDateSelected(day) ? "selected" : ""} ${isDateDisabled(day) ? "disabled" : ""}`}
            onClick={() => handleDateClick(day)}
            disabled={isDateDisabled(day)}
          >
            {day}
          </button>
        ))}
      </div>
    </div>
  );
}

